import clsx from 'clsx';
import { isObject } from 'lodash-es';
import { Fragment, ReactNode, useState } from 'react';
import { Collapse, IconProps, Nav } from '../index';
import { useAppShellTheme } from './theme-provider';

export type NavbarVerticalMenuRoute<T = string, TFeature = string> = {
  name: T;
  to: string;
  dataCy?: string;
  exact?: boolean;
  externalTarget?: boolean;
  icon?: IconProps['icon'];
  features?: Array<TFeature>;
  roles?: Array<string>;
  children?: Array<NavbarVerticalMenuRoute<T, TFeature>>;
};

export type NavbarVerticalMenuSeparator<T = string> = {
  name: T;
  separator: true;
};

export type NavbarVerticalMenuProps<T = string, TFeature = string> = {
  routes: Array<
    NavbarVerticalMenuRoute<T, TFeature> | NavbarVerticalMenuSeparator<T>
  >;
  renderMenuItem: (route: NavbarVerticalMenuRoute<T, TFeature>) => ReactNode;
  renderLink: (
    route: NavbarVerticalMenuRoute<T, TFeature>,
    children: ReactNode,
  ) => ReactNode;
  renderSeparator: (item: NavbarVerticalMenuSeparator<T>) => ReactNode;
  onItemClick?: () => void;
  shouldRender?: (route: NavbarVerticalMenuRoute<T, TFeature>) => boolean;
  getCurrentPath?: () => string;
};

function isSeparator<T>(arg: unknown): arg is NavbarVerticalMenuSeparator<T> {
  return isObject(arg) && 'separator' in arg && arg.separator === true;
}

type CollapseItemsProps<T, TFeature> = {
  route: NavbarVerticalMenuRoute<T, TFeature>;
  renderMenuItem: (route: NavbarVerticalMenuRoute<T, TFeature>) => ReactNode;
  renderLink: (
    route: NavbarVerticalMenuRoute<T, TFeature>,
    children: ReactNode,
  ) => ReactNode;
  shouldRender?: (route: NavbarVerticalMenuRoute<T, TFeature>) => boolean;
  getCurrentPath?: () => string;
  onItemClick?: () => void;
};

const CollapseItems = <T, TFeature>({
  route,
  renderMenuItem,
  renderLink,
  shouldRender,
  getCurrentPath,
  onItemClick,
}: CollapseItemsProps<T, TFeature>) => {
  const currentPath = getCurrentPath?.() ?? '';

  const openCollapse = (
    children: Array<NavbarVerticalMenuRoute<T, TFeature>>,
  ) => {
    const checkLink = (
      child: NavbarVerticalMenuRoute<T, TFeature>,
    ): boolean => {
      if (child.to === currentPath) {
        return true;
      }
      return child.children?.some(checkLink) ?? false;
    };
    return children.some(checkLink);
  };

  const [open, setOpen] = useState<boolean>(openCollapse(route.children || []));

  return (
    <Nav.Item as='li' className='has-children'>
      <Nav.Link
        onClick={() => {
          setOpen(!open);
        }}
        className={clsx('dropdown-indicator cursor-pointer')}
        aria-expanded={open}
        data-cy={route.dataCy}
      >
        {renderMenuItem(route)}
      </Nav.Link>
      <Collapse in={open}>
        <Nav className='flex-column nav' as='ul'>
          {route.children && (
            <NavbarVerticalMenu
              routes={route.children}
              renderMenuItem={renderMenuItem}
              renderLink={renderLink}
              renderSeparator={() => null}
              shouldRender={shouldRender}
              getCurrentPath={getCurrentPath}
              onItemClick={onItemClick}
            />
          )}
        </Nav>
      </Collapse>
    </Nav.Item>
  );
};

export const NavbarVerticalMenu = <T, TFeature>({
  routes,
  renderMenuItem,
  renderLink,
  renderSeparator,
  shouldRender,
  onItemClick,
  getCurrentPath,
}: NavbarVerticalMenuProps<T, TFeature>) => {
  const { showBurgerMenu, isNavbarVerticalCollapsed, setConfig } =
    useAppShellTheme();

  const handleNavItemClick = () => {
    if (showBurgerMenu) {
      setConfig('showBurgerMenu', !showBurgerMenu);
      setConfig('isNavbarVerticalCollapsed', !isNavbarVerticalCollapsed);
    }
    onItemClick?.();
  };

  return (
    <>
      {routes.map((route) => {
        const isSep = isSeparator(route);
        if (shouldRender && !isSep && !shouldRender(route)) {
          return (
            <Fragment
              key={
                isSep ? `${String(route.name)}.separator` : String(route.name)
              }
            />
          );
        }

        if (isSep) {
          return (
            <Fragment key={`${String(route.name)}.separator`}>
              {renderSeparator(route)}
            </Fragment>
          );
        }

        if (!route.children) {
          return (
            <Nav.Item
              as='li'
              key={String(route.name)}
              onClick={handleNavItemClick}
            >
              {renderLink(route, renderMenuItem(route))}
            </Nav.Item>
          );
        }

        return (
          <CollapseItems
            route={route}
            key={String(route.name)}
            renderMenuItem={renderMenuItem}
            renderLink={renderLink}
            shouldRender={shouldRender}
            getCurrentPath={getCurrentPath}
            onItemClick={onItemClick}
          />
        );
      })}
    </>
  );
};
